import { useEffect } from 'react';
import { AppProviders } from './providers';
import { AppRoutes } from '@/routes';
import { useAppDispatch } from '@/hooks/useAppDispatch';
import { restoreSession, setInitialized } from '@/store/authSlice';
import { purgeLegacyAuthFromBrowser } from '@/core/security/purgeLegacyAuthStorage';
import { STORAGE_KEYS } from '@/constants/storage';
import { identityRestoreSession, setIdentityInitialized } from '@/store/identitySlice';
import { shouldSkipColdSessionBootstrap } from '@/utils/sessionBootstrap';

let coldBootstrapStarted = false;

function AppInitializer({ children }: { children: React.ReactNode }) {
  const dispatch = useAppDispatch();

  useEffect(() => {
    purgeLegacyAuthFromBrowser();

    const finishBootstrap = () => {
      dispatch(setInitialized());
      dispatch(setIdentityInitialized());
      const token = localStorage.getItem(STORAGE_KEYS.IDENTITY_TOKEN);
      if (token) {
        void dispatch(identityRestoreSession());
      }
    };

    if (shouldSkipColdSessionBootstrap(window.location.pathname)) {
      finishBootstrap();
      return;
    }

    if (coldBootstrapStarted) {
      return;
    }
    coldBootstrapStarted = true;

    void dispatch(restoreSession())
      .unwrap()
      .catch(() => {
        // Unauthenticated cold start is expected; bootstrapSession.rejected clears client state.
      })
      .finally(() => {
        finishBootstrap();
      });
  }, [dispatch]);

  return <>{children}</>;
}

export default function App() {
  return (
    <AppProviders>
      <AppInitializer>
        <AppRoutes />
      </AppInitializer>
    </AppProviders>
  );
}
